//libs
import React, { memo, ReactElement, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

//components
import ErrorComponent from 'shared/layouts/Listing/ErrorWrapperV2/components/ErrorComponent';
import AdPlatformErrorWrapper from 'shared/layouts/Listing/ErrorWrapperV2/components/AdPlatformErrorWrapper';

//constants
import { ERROR_PAGES_TEXTS } from './constants';

//styles
import { Wrapper } from './styles';

//helpers
import { isIntegerId } from 'shared/helpers/utils';
import { getCategoryId, getIsFilteredURL, getSearchText } from 'shared/store/listingV2/helpers';
import { getStoreUrl } from 'shared/helpers/storeUrl';

//selectors
import {
  getIsFetchingError,
  getIsNotFound,
  getLocation,
  getTotalCount,
  getIsFetching,
  getAppliedFilters,
  getStoreRedirect,
  getStoreRedirectCount,
} from 'shared/store/listingV2/selectors';

//hooks
import { useFilters } from 'shared/store/listingV2/hooks';
import { useDomainConfig } from 'shared/domainConfig/context/context';

//actions
import { updateStatusCode } from 'shared/store/app/actions';
import { onNoResults } from '../components/Analytics/helpers';
import StoreBanner from 'shared/components/StoreBanner';

interface ErrorWrapperProps {
  children: ReactElement;
}

function ErrorWrapper({ children }: ErrorWrapperProps) {
  const { search, pathname } = useSelector(getLocation);
  const isFetchingError = useSelector(getIsFetchingError);
  const isNotFound = useSelector(getIsNotFound);
  const isSearch = getSearchText(search).trim().length > 0;
  const totalProducts = useSelector(getTotalCount);
  const isFilteredUrl = getIsFilteredURL(search);
  const searchText = getSearchText(search);
  const categoryId = getCategoryId(pathname) || '';
  const isFetching = useSelector(getIsFetching);
  const appliedFilters = useSelector(getAppliedFilters);
  const storeRedirect = useSelector(getStoreRedirect);
  const redirectStoreProductCount = useSelector(getStoreRedirectCount);
  const { updateAppliedFilter } = useFilters();
  const { PRODUCT_NOT_FOUND, GENERIC_ERROR_IMAGE } = useDomainConfig();

  const dispatch = useDispatch();
  const hasTrackedNoResults = useRef(false);

  // Fire analytics event only once when no results condition is met
  useEffect(() => {
    if (!isFetching && totalProducts === 0 && !hasTrackedNoResults.current) {
      onNoResults(isSearch);
      hasTrackedNoResults.current = true;
    }
  }, [isFetching, totalProducts, isSearch]);

  const redirectToHome = () => {
    window.location.href = getStoreUrl('/?source=plp-error');
    //todo @listingV2
    //pending with analytics
  };

  const onResetClick = () => {
    const appliedValues: Record<string, string> = {};
    if (appliedFilters.sort) appliedValues.sort = appliedFilters.sort;
    updateAppliedFilter(appliedValues);
  };

  if (categoryId !== '' && !isIntegerId(categoryId)) {
    dispatch(updateStatusCode(404));
    return (
      <ErrorComponent
        errorDetails={{ ...ERROR_PAGES_TEXTS.PAGE_NOT_FOUND, imgSrc: GENERIC_ERROR_IMAGE }}
        onButtonClick={redirectToHome}
      />
    );
  }

  if (!isFetching) {
    if (isNotFound) {
      return (
        <ErrorComponent
          errorDetails={{ ...ERROR_PAGES_TEXTS.PAGE_404, imgSrc: GENERIC_ERROR_IMAGE }}
          onButtonClick={redirectToHome}
        />
      );
    }

    if (isFetchingError) {
      return (
        <ErrorComponent
          errorDetails={{ ...ERROR_PAGES_TEXTS.SOMETHING_WENT_WRONG, imgSrc: GENERIC_ERROR_IMAGE }}
          onButtonClick={redirectToHome}
        />
      );
    }

    if (totalProducts === 0 && isFilteredUrl) {
      return (
        <>
          <ErrorComponent
            errorDetails={{
              ...ERROR_PAGES_TEXTS.NO_RESULT_FOUND_WITH_FILTERS,
              imgSrc: PRODUCT_NOT_FOUND,
            }}
            onButtonClick={onResetClick}
          />
        </>
      );
    }

    if (totalProducts === 0 && isSearch) {
      return (
        <>
          <StoreBanner
            isSearch={isSearch}
            currentStoreProductCount={totalProducts}
            redirectStoreProductCount={redirectStoreProductCount}
            storeToRedirect={storeRedirect}
            productName={searchText}
          />
          <AdPlatformErrorWrapper
            errorDetails={{
              ...ERROR_PAGES_TEXTS.SEARCH_AGAIN(searchText),
              imgSrc: PRODUCT_NOT_FOUND,
            }}
            searchPage
          />
        </>
      );
    }

    if (totalProducts === 0) {
      return (
        <>
          <AdPlatformErrorWrapper
            errorDetails={{ ...ERROR_PAGES_TEXTS.NO_RESULT_FOUND, imgSrc: PRODUCT_NOT_FOUND }}
            onButtonClick={redirectToHome}
          />
        </>
      );
    }
  }

  return <Wrapper>{children}</Wrapper>;
}

export default memo(ErrorWrapper);
